import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);
let authCheckPromise = null;
let socketTokenPromise = null;
let socketTokenValue = null;

const isBootTimingDebug = () => {
  try {
    return localStorage.getItem('whisper_debug_boot_timing') === 'true';
  } catch {
    return false;
  }
};

const logBootTiming = (event, extra = {}) => {
  if (!isBootTimingDebug()) return;
  console.info('[whisper boot]', event, {
    t: Math.round(performance.now()),
    ...extra,
  });
};

const clearSocketTokenCache = () => {
  socketTokenPromise = null;
  socketTokenValue = null;
};

// Wipe account-scoped cached state so a different account on the same browser
// never inherits the previous account's conversation list, message caches,
// drafts, or selected chat. Preserves non-account debug flags.
const KEEP_STORAGE_KEYS = new Set([
  'whisper_debug_boot_timing',
  'whisper_debug_send_timing',
]);

const clearAccountScopedStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith('whisper_') && !KEEP_STORAGE_KEYS.has(key)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Storage access can fail (private mode / quota); never block logout.
  }
};

const requestSocketToken = () => {
  if (socketTokenValue) return Promise.resolve(socketTokenValue);
  if (socketTokenPromise) return socketTokenPromise;

  logBootTiming('socket-token:start');
  socketTokenPromise = axios.get(`${BACKEND_URL}/api/auth/socket-token`, {
    withCredentials: true,
  }).then((response) => {
    socketTokenValue = response.data.token;
    logBootTiming('socket-token:success');
    return socketTokenValue;
  }).catch((error) => {
    clearSocketTokenCache();
    logBootTiming('socket-token:error', { message: error?.message });
    throw error;
  });

  return socketTokenPromise;
};

const requestCurrentUser = () => {
  if (authCheckPromise) return authCheckPromise;

  logBootTiming('auth:start');
  authCheckPromise = axios.get(`${BACKEND_URL}/api/auth/me`, {
    withCredentials: true
  }).then((response) => {
    logBootTiming('auth:success');
    requestSocketToken().catch(() => {});
    return response.data;
  }).catch((error) => {
    clearSocketTokenCache();
    logBootTiming('auth:error', { message: error?.message });
    throw error;
  }).finally(() => {
    authCheckPromise = null;
  });

  return authCheckPromise;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(false);

  const checkAuth = useCallback(async () => {
    try {
      const authUser = await requestCurrentUser();
      if (!mountedRef.current) return authUser;
      setUser(authUser);
      return authUser;
    } catch (error) {
      if (!mountedRef.current) return false;
      setUser(false);
      return false;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  // Reconcile mutable account/profile fields without turning a temporary network
  // failure into a logout. Initial authentication still uses checkAuth(); foreground
  // syncs use this non-destructive refresher.
  const refreshUser = useCallback(async () => {
    const authUser = await requestCurrentUser();
    if (mountedRef.current) {
      setUser((previous) => (
        previous && previous !== false ? { ...previous, ...authUser } : authUser
      ));
    }
    return authUser;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    checkAuth();
    return () => {
      mountedRef.current = false;
    };
  }, [checkAuth]);

  const login = useCallback(async (username, password) => {
    clearSocketTokenCache();
    const response = await axios.post(
      `${BACKEND_URL}/api/auth/login`,
      { username, password },
      { withCredentials: true }
    );
    clearAccountScopedStorage();
    requestSocketToken().catch(() => {});
    setUser(response.data);
    return response.data;
  }, []);

  const register = useCallback(async (username, password) => {
    clearSocketTokenCache();
    const response = await axios.post(
      `${BACKEND_URL}/api/auth/register`,
      { username, password },
      { withCredentials: true }
    );
    clearAccountScopedStorage();
    requestSocketToken().catch(() => {});
    setUser(response.data);
    return response.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    clearSocketTokenCache();
    clearAccountScopedStorage();
    setUser(false);
  }, []);

  const getSocketToken = useCallback(() => requestSocketToken(), []);

  const updateProfilePicture = useCallback(async (file) => {
    const response = await axios.post(
      `${BACKEND_URL}/api/auth/profile-picture`,
      file,
      {
        withCredentials: true,
        headers: { 'Content-Type': file.type }
      }
    );
    setUser(prev => ({
      ...prev,
      profile_picture: response.data.profile_picture,
      profile_picture_thumb: response.data.profile_picture_thumb,
      profile_picture_updated_at: response.data.profile_picture_updated_at,
    }));
    return response.data;
  }, []);

  const updateProfile = useCallback(async (data) => {
    const response = await axios.put(
      `${BACKEND_URL}/api/auth/profile`,
      data,
      { withCredentials: true }
    );
    setUser(prev => ({ ...prev, ...response.data }));
    return response.data;
  }, []);

  // Memoized so a provider re-render (e.g. refreshUser reconciliation) doesn't
  // produce a brand-new context object and re-render every consumer.
  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    updateProfilePicture,
    updateProfile,
    checkAuth,
    refreshUser,
    getSocketToken,
    setUser,
  }), [user, loading, login, register, logout, updateProfilePicture, updateProfile, checkAuth, refreshUser, getSocketToken, setUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
